import { makeAutoObservable } from 'mobx';
import { CompanyEditFormData } from '../../../model/professional/compagny-model';
import companyStore from '../../../store/company-store';
import compagnyModel from '../../../model/professional/compagny-model';

export class CompanyEditFormViewModel {

  formData: CompanyEditFormData = {
    id: '',
    businessName: '',
    description: '',
    address: '',
    city: '',
    postalCode: '',
    country: '',
    siren: '',
    sirenVerified: false,
    reservationConditions: '',
    paymentMethods: [],
    companyLogo: null,
    galleryPhoto: null,
    domain: null,
    ownerId: '',
    collaboratorsCount: '0',
    phone: '',
    email: '',
    logo: null,
  };

  errors: Partial<Record<keyof CompanyEditFormData, string>> = {};

  successMessages: Partial<Record<keyof CompanyEditFormData, string>> = {};

  isLoading: boolean = true;

  isVerifyingSiren: boolean = false;

  galleryPhotoFiles: File[] = [];

  galleryPhotoPreviewUrls: string[] = [];

  logoPreviewUrl: string | null = null;

  originalGalleryPhotos: string[] = [];

  deletedPhotoIndices: number[] = [];

  private businessNameCheckTimer: NodeJS.Timeout | null = null;

  private initialBusinessName: string = '';

  private static instance: CompanyEditFormViewModel;

  private constructor() {
    makeAutoObservable(this);
  }

  public static getInstance(): CompanyEditFormViewModel {
    if (!CompanyEditFormViewModel.instance) {
      CompanyEditFormViewModel.instance = new CompanyEditFormViewModel();
    }
    return CompanyEditFormViewModel.instance;
  }

  setFormData(data: Partial<CompanyEditFormData>) {
    this.formData = { ...this.formData, ...data };
  }

  setErrors(errors: Partial<Record<keyof CompanyEditFormData, string>>) {
    this.errors = errors;
  }

  setError(field: keyof CompanyEditFormData, message: string) {
    this.errors = { ...this.errors, [field]: message };
  }

  clearError(field: keyof CompanyEditFormData) {
    const newErrors = { ...this.errors };
    delete newErrors[field];
    this.errors = newErrors;
  }

  setSuccessMessage(field: keyof CompanyEditFormData, message: string) {
    this.successMessages = { ...this.successMessages, [field]: message };
  }

  clearSuccessMessage(field: keyof CompanyEditFormData) {
    const newSuccessMessages = { ...this.successMessages };
    delete newSuccessMessages[field];
    this.successMessages = newSuccessMessages;
  }

  setIsLoading(loading: boolean) {
    this.isLoading = loading;
  }

  setIsVerifyingSiren(verifying: boolean) {
    this.isVerifyingSiren = verifying;
  }

  cleanup = () => {
    this.galleryPhotoPreviewUrls.forEach(url => {
      if (url && url.startsWith('blob:')) {
        URL.revokeObjectURL(url);
      }
    });

    if (this.logoPreviewUrl && this.logoPreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(this.logoPreviewUrl);
    }

    if (this.businessNameCheckTimer) {
      clearTimeout(this.businessNameCheckTimer);
      this.businessNameCheckTimer = null;
    }

    this.galleryPhotoPreviewUrls = [];
    this.galleryPhotoFiles = [];
    this.originalGalleryPhotos = [];
    this.deletedPhotoIndices = [];
    this.logoPreviewUrl = null;
    this.initialBusinessName = '';
  };

  initializeForm = () => {
    this.cleanup();

    const company = companyStore.getCompanyById;

    if (!company) {
      console.warn("Aucune entreprise sélectionnée dans le store");
      this.setIsLoading(false);
      return;
    }

    this.formData = {
      id: company.id || '',
      businessName: company.businessName || '',
      description: company.description || '',
      domain: company.domain || null,
      address: company.address || '',
      city: company.city || '',
      postalCode: company.postalCode || '',
      country: company.country || '',
      siren: company.siren || '',
      sirenVerified: company.sirenVerified || false,
      reservationConditions: company.reservationConditions || '',
      paymentMethods: company.paymentMethods || [],
      companyLogo: company.companyLogo || null,
      galleryPhoto: company.galleryPhoto || null,
      ownerId: company.ownerId || '',
      collaboratorsCount: company.collaboratorsCount || '0',
      phone: company.phone || '',
      email: company.email || '',
      logo: null,
    };

    this.initialBusinessName = company.businessName || '';

    if (company.galleryPhoto) {
      const photos = typeof company.galleryPhoto === 'string'
        ? company.galleryPhoto.split(',').filter(url => url.trim())
        : company.galleryPhoto;
      this.galleryPhotoPreviewUrls = photos;
      this.originalGalleryPhotos = [...photos];
    }

    this.setIsLoading(false);
  };

  handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    this.setFormData({ [name]: value });

    if (this.errors[name as keyof CompanyEditFormData]) {
      this.clearError(name as keyof CompanyEditFormData);
    }
    if (this.successMessages[name as keyof CompanyEditFormData]) {
      this.clearSuccessMessage(name as keyof CompanyEditFormData);
    }

    if (name === 'businessName' && value.trim().length > 2) {
      this.checkBusinessNameAvailabilityDebounced(value.trim());
    }
  };

  checkBusinessNameAvailabilityDebounced = (businessName: string) => {
    if (this.businessNameCheckTimer) {
      clearTimeout(this.businessNameCheckTimer);
    }

    if (businessName === this.initialBusinessName) {
      this.clearError('businessName');
      this.clearSuccessMessage('businessName');
      return;
    }

    this.businessNameCheckTimer = setTimeout(async () => {
      try {
        const result = await compagnyModel.checkBusinessNameAvailability(businessName, this.formData.id);

        if (!result.available) {
          this.setError('businessName', result.message);
          this.clearSuccessMessage('businessName');
        } else {
          this.clearError('businessName');
          this.setSuccessMessage('businessName', result.message);
        }
      } catch (error) {
        console.error('Error checking business name availability:', error);
      }
    }, 500);
  };

  handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      if (file.size > 2 * 1024 * 1024) {
        this.setError('logo', 'Le logo ne doit pas dépasser 2MB');
        return;
      }

      if (!file.type.match(/^image\/(jpeg|jpg|png|webp)$/)) {
        this.setError('logo', 'Le logo doit être au format JPG, PNG ou WEBP');
        return;
      }

      this.setFormData({ logo: file });

      if (this.logoPreviewUrl) {
        URL.revokeObjectURL(this.logoPreviewUrl);
      }
      this.logoPreviewUrl = URL.createObjectURL(file);
      this.clearError('logo');
    }
  };

  removeLogo = () => {
    if (this.logoPreviewUrl) {
      URL.revokeObjectURL(this.logoPreviewUrl);
      this.logoPreviewUrl = null;
    }
    this.setFormData({ companyLogo: null, logo: null });
  };

  verifySiren = async (t: any) => {
    if (!this.formData.siren || this.formData.siren.length !== 9) {
      this.setError('siren', t('company.form.errors.sirenInvalid', 'Le SIREN doit contenir 9 chiffres') as string);
      return;
    }

    try {
      this.setIsVerifyingSiren(true);

      const result = await compagnyModel.verifySiren(this.formData.siren);

      if (result.success && result.isValid) {
        this.setFormData({ sirenVerified: true });
        this.clearError('siren');
      } else {
        this.setError('siren', result.message || 'SIREN invalide');
      }

      this.setIsVerifyingSiren(false);
    } catch (error) {
      console.error('Erreur lors de la vérification du SIREN:', error);
      this.setError('siren', 'Erreur lors de la vérification du SIREN');
      this.setIsVerifyingSiren(false);
    }
  };

  validateForm = (t: any): boolean => {
    const newErrors: Partial<Record<keyof CompanyEditFormData, string>> = {};

    if (!this.formData.businessName.trim()) {
      newErrors.businessName = t('company.form.errors.businessNameRequired', 'Le nom de l\'entreprise est requis') as string;
    }

    if (!this.formData.domain || !this.formData.domain.trim()) {
      newErrors.domain = t('company.form.errors.domainRequired', 'Le domaine d\'activité est requis') as string;
    }

    if (!this.formData.address.trim()) {
      newErrors.address = t('company.form.errors.addressRequired', 'L\'adresse est requise') as string;
    }

    if (!this.formData.city.trim()) {
      newErrors.city = t('company.form.errors.cityRequired', 'La ville est requise') as string;
    }

    if (!this.formData.postalCode.trim()) {
      newErrors.postalCode = t('company.form.errors.postalCodeRequired', 'Le code postal est requis') as string;
    }

    if (!this.formData.country.trim()) {
      newErrors.country = t('company.form.errors.countryRequired', 'Le pays est requis') as string;
    }

    if (!this.formData.siren.trim()) {
      newErrors.siren = t('company.form.errors.sirenRequired', 'Le numéro SIREN est requis') as string;
    } else if (!/^\d{9}$/.test(this.formData.siren)) {
      newErrors.siren = t('company.form.errors.sirenInvalid', 'Le SIREN doit contenir 9 chiffres') as string;
    }

    this.setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  handleSubmit = async (e: React.FormEvent, onSubmit: (data: CompanyEditFormData) => void, t: any) => {
    e.preventDefault();

    if (this.validateForm(t)) {
      onSubmit(this.formData);
    }
  };

  handlePaymentMethodChange = (method: string, checked: boolean) => {
    const updatedMethods = checked
      ? [...this.formData.paymentMethods, method]
      : this.formData.paymentMethods.filter(m => m !== method);

    this.setFormData({ paymentMethods: updatedMethods });
  };

  handleGalleryPhotosChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      const maxPhotos = 5;

      if (this.galleryPhotoFiles.length + this.galleryPhotoPreviewUrls.length + files.length > maxPhotos) {
        this.setError('galleryPhoto', `Vous ne pouvez ajouter que ${maxPhotos} photos maximum`);
        return;
      }

      const validFiles: File[] = [];
      for (const file of files) {
        if (file.size > 5 * 1024 * 1024) {
          this.setError('galleryPhoto', 'Chaque photo ne doit pas dépasser 5MB');
          continue;
        }

        if (!file.type.match(/^image\/(jpeg|jpg|png|webp)$/)) {
          this.setError('galleryPhoto', 'Les photos doivent être au format JPG, PNG ou WEBP');
          continue;
        }

        validFiles.push(file);
      }

      if (validFiles.length > 0) {
        this.galleryPhotoFiles = [...this.galleryPhotoFiles, ...validFiles];

        validFiles.forEach(file => {
          this.galleryPhotoPreviewUrls.push(URL.createObjectURL(file));
        });

        this.clearError('galleryPhoto');
      }
    }
  };

  removeGalleryPhoto = (index: number) => {
    const url = this.galleryPhotoPreviewUrls[index];

    const isBlobUrl = url && url.startsWith('blob:');

    if (isBlobUrl) {
      
      let blobCount = 0;
      let fileIndex = -1;

      for (let i = 0; i <= index; i++) {
        if (this.galleryPhotoPreviewUrls[i].startsWith('blob:')) {
          if (i === index) {
            fileIndex = blobCount;
          }
          blobCount++;
        }
      }

      URL.revokeObjectURL(url);

      if (fileIndex !== -1) {
        this.galleryPhotoFiles = this.galleryPhotoFiles.filter((_, i) => i !== fileIndex);
      }
    } else {
      const originalIndex = this.originalGalleryPhotos.indexOf(url);
      if (originalIndex !== -1) {
        this.deletedPhotoIndices.push(originalIndex);
      }
    }

    this.galleryPhotoPreviewUrls = this.galleryPhotoPreviewUrls.filter((_, i) => i !== index);
  };
}

export default CompanyEditFormViewModel.getInstance();
